import React, { useState, useCallback, useMemo } from "react";
import { Listbox, Combobox, Icon, BlockStack, Text } from "@medentic/polaris-react";
import { SearchIcon } from "@medentic/polaris-icons";
import { useTranslation } from "react-i18next";

export interface SingleSelectComboboxOption {
  label: string;
  value: string;
  displayOrder?: number;
}

interface SingleSelectComboboxProps {
  options: SingleSelectComboboxOption[];
  value: string;
  onChange: (value: string) => void;
  label: string;
  disabled?: boolean;
  helpText?: string;
  error?: string;
}

const SingleSelectCombobox: React.FC<SingleSelectComboboxProps> = ({
  options,
  value,
  onChange,
  label,
  disabled = false,
  helpText,
  error,
}) => {
  const { t } = useTranslation();
  const sortedOptions = useMemo(
    () =>
      [...options].sort(
        (a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0)
      ),
    [options]
  );

  const [queryValue, setQueryValue] = useState("");

  const selectedOptionLabel = useMemo(() => {
    const selectedOption = sortedOptions.find((option) => option.value === value);
    return selectedOption ? selectedOption.label : "";
  }, [sortedOptions, value]);

  const inputValue = queryValue || selectedOptionLabel;

  const escapeSpecialRegExCharacters = useCallback(
    (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"),
    []
  );

  const filteredOptions = useMemo(() => {
    if (queryValue === "") {
      return sortedOptions.slice(0, 10);
    }

    const filterRegex = new RegExp(escapeSpecialRegExCharacters(queryValue), "i");
    return sortedOptions.filter((option) => option.label.match(filterRegex));
  }, [sortedOptions, queryValue, escapeSpecialRegExCharacters]);

  const updateText = useCallback(
    (value: string) => {
      setQueryValue(value);
    },
    []
  );

  const updateSelection = useCallback(
    (selected: string) => {
      onChange(selected);
      setQueryValue("");
    },
    [onChange]
  );

  const optionsMarkup =
    filteredOptions.length > 0
      ? filteredOptions.map((option) => {
          const { label, value: optionValue } = option;
          return (
            <Listbox.Option
              key={optionValue}
              value={optionValue}
              selected={optionValue === value}
              accessibilityLabel={label}
            >
              {label}
            </Listbox.Option>
          );
        })
      : null;

  const activator = useMemo(
    () => (
      <Combobox.TextField
        prefix={<Icon source={SearchIcon} />}
        onChange={updateText}
        label={label}
        labelHidden
        value={inputValue}
        placeholder={t("common.search")}
        autoComplete="off"
        disabled={disabled}
        helpText={helpText}
        error={error}
        clearButton
        onClearButtonClick={() => {
          setQueryValue("");
          onChange("");
        }}
      />
    ),
    [updateText, label, inputValue, t, disabled, helpText, error, onChange]
  );

  return (
    <BlockStack gap="100">
      <Text variant="bodyMd" as="span" tone={disabled ? "disabled" : undefined}>
        {label}
      </Text>
      <Combobox
        activator={activator}
      >
        {filteredOptions.length > 0 ? (
          <Listbox onSelect={updateSelection}>{optionsMarkup}</Listbox>
        ) : null}
      </Combobox>
    </BlockStack>
  );
};

export default SingleSelectCombobox;
